/**
 * Motobilt Search UI Controller
 * Updated for Search Drawer (side panel) integration
 */

(function() {
  'use strict';

  class MotobiltSearchUI {
    constructor() {
      this.drawerInput = null;
      this.drawerResults = null;
      this.drawerInitial = null;
      this.drawerForm = null;
      this.isSearching = false;
      this.selectedIndex = -1;
      this.currentQuery = '';
      this.searchDebounce = null;
    }

    // ============================================
    // DRAWER SEARCH INITIALIZATION
    // ============================================

    initDrawer(options = {}) {
      this.drawerInput = document.querySelector(options.inputSelector || '#side-panel-search-input');
      this.drawerResults = document.querySelector(options.resultsSelector || '[data-search-results]');
      this.drawerInitial = document.querySelector(options.initialSelector || '[data-search-initial]');
      this.drawerForm = this.drawerInput?.closest('form');

      if (!this.drawerInput) {
        console.warn('[Motobilt Search] Drawer input not found');
        return this;
      }

      console.log('[Motobilt Search] Initializing drawer search');

      // Preload products when drawer opens or on first focus
      let productsLoaded = false;

      const preloadProducts = async () => {
        if (!productsLoaded) {
          productsLoaded = true;
          try {
            await window.MotobiltSearch.loadProducts();
            console.log('[Motobilt Search] Products preloaded');
          } catch (e) {
            console.error('Failed to preload products', e);
          }
        }
      };

      // Preload on focus
      this.drawerInput.addEventListener('focus', preloadProducts, { once: true });

      // Also try to preload when drawer becomes visible
      const drawer = document.querySelector('#Search-Drawer');
      if (drawer) {
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.attributeName === 'class' || mutation.attributeName === 'style') {
              const isVisible = drawer.classList.contains('active') ||
                               drawer.classList.contains('is-open') ||
                               drawer.style.display !== 'none';
              if (isVisible) {
                preloadProducts();
              }
            }
          });
        });
        observer.observe(drawer, { attributes: true });
      }

      // Input handler with debounce
      this.drawerInput.addEventListener('input', (e) => {
        clearTimeout(this.searchDebounce);
        this.searchDebounce = setTimeout(() => {
          this._handleDrawerInput(e.target.value);
        }, 150);
      });

      // Keyboard navigation
      this.drawerInput.addEventListener('keydown', (e) => this._handleKeydown(e));

      // Form submit - go to search results page
      if (this.drawerForm) {
        this.drawerForm.addEventListener('submit', (e) => {
          e.preventDefault();
          this._submitSearch(this.drawerInput.value);
        });
      }

      // Check if there's already a query in the input
      if (this.drawerInput.value.trim()) {
        this._handleDrawerInput(this.drawerInput.value);
      }

      return this;
    }

    async _handleDrawerInput(query) {
      this.currentQuery = query.trim();
      this.selectedIndex = -1;

      // If empty, show initial content
      if (!this.currentQuery) {
        this._showInitialContent();
        return;
      }

      // Need at least 2 characters
      if (this.currentQuery.length < 2) {
        return;
      }

      // Show loading state
      this._showLoading();

      try {
        const results = await window.MotobiltSearch.quickSearch(this.currentQuery);
        this._renderDrawerResults(results);
      } catch (error) {
        console.error('Search error:', error);
        this._renderError();
      }
    }

    _handleKeydown(e) {
      const items = this.drawerResults?.querySelectorAll('[data-item-index]') || [];

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          this.selectedIndex = Math.min(this.selectedIndex + 1, items.length - 1);
          this._highlightItem(items);
          break;

        case 'ArrowUp':
          e.preventDefault();
          this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
          this._highlightItem(items);
          break;

        case 'Enter':
          if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
            e.preventDefault();
            const link = items[this.selectedIndex].querySelector('a');
            if (link) window.location.href = link.href;
          }
          // If no item selected, form submits normally (goes to search page)
          break;

        case 'Escape':
          this._showInitialContent();
          this.drawerInput.value = '';
          this.currentQuery = '';
          break;
      }
    }

    _highlightItem(items) {
      items.forEach((item, idx) => {
        item.classList.toggle('is-highlighted', idx === this.selectedIndex);
      });

      // Scroll highlighted item into view
      if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
        items[this.selectedIndex].scrollIntoView({ block: 'nearest' });
      }
    }

    // ============================================
    // DRAWER CONTENT RENDERING
    // ============================================

    _showInitialContent() {
      if (this.drawerInitial) {
        this.drawerInitial.style.display = 'block';
      }
      if (this.drawerResults) {
        this.drawerResults.style.display = 'none';
        this.drawerResults.innerHTML = '';
      }
    }

    _showResultsContent() {
      if (this.drawerInitial) {
        this.drawerInitial.style.display = 'none';
      }
      if (this.drawerResults) {
        this.drawerResults.style.display = 'block';
      }
    }

    _renderDrawerResults(results) {
      if (!this.drawerResults) return;

      this._showResultsContent();

      let html = '<div class="thb-predictive-search">';
      let itemIndex = 0;

      // Typo correction notice
      if (results.correctedQuery) {
        html += `
          <div class="mb-search__correction">
            Showing results for <strong>${results.correctedQuery}</strong>
          </div>
        `;
      }

      // Recent searches that match
      if (results.recentSearches?.length > 0) {
        html += `
          <div class="thb-predictive-search--popular">
            <span class="thb-predictive-search--label">Recent Searches</span>
            <ul>
              ${results.recentSearches.map(term => `
                <li><a href="/search?q=${encodeURIComponent(term)}">${term}</a></li>
              `).join('')}
            </ul>
          </div>
        `;
      }

      // Predictive query suggestions (phrases, not products)
      if (results.querySuggestions?.length > 0) {
        html += `
          <div class="thb-predictive-search--popular">
            <span class="thb-predictive-search--label">Suggested Searches</span>
            <ul>
              ${results.querySuggestions.map(s => `
                <li><a href="/search?q=${encodeURIComponent(s.text)}">${this._highlightMatch(s.text, this.currentQuery)}</a></li>
              `).join('')}
            </ul>
          </div>
        `;
      }

      // Category suggestions
      if (results.suggestions?.length > 0) {
        html += `
          <div class="thb-predictive-search--popular">
            <span class="thb-predictive-search--label">Categories</span>
            <ul>
              ${results.suggestions.map(s => `
                <li><a href="/search?q=${encodeURIComponent(s.text)}">${s.text} (${s.count})</a></li>
              `).join('')}
            </ul>
          </div>
        `;
      }

      // Products
      if (results.products?.length > 0) {
        html += `
          <div class="thb-predictive-search--promotion">
            <span class="thb-predictive-search--label">Products</span>
            <div class="mb-search__products">
              ${results.products.map(product => `
                <div data-item-index="${itemIndex++}">
                  ${this._renderCompactProduct(product)}
                </div>
              `).join('')}
            </div>
          </div>
        `;

        // View all link
        if (results.totalResults > results.products.length) {
          html += `
            <div class="mb-search__footer">
              <a href="/search?q=${encodeURIComponent(this.currentQuery)}" class="mb-search__view-all">
                View all ${results.totalResults} results
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </a>
            </div>
          `;
        }
      }

      // No results
      if (!results.products?.length && this.currentQuery) {
        html += `
          <div class="mb-search__empty">
            <p>No results for "<strong>${this.currentQuery}</strong>"</p>
            <p class="mb-search__empty-hint">Check spelling or try different keywords</p>
          </div>
        `;
      }

      html += '</div>';

      this.drawerResults.innerHTML = html;
    }

    // Full theme-native product card, matching snippets/product-card-2p0.liquid
    // exactly (same tag names/classes) so the theme's own product-grid.css
    // styling and app.js <product-card>/<quick-view> custom elements pick it
    // up automatically - real Add to Cart AJAX and Quick View, no extra wiring.
    _renderProductCard(product) {
      const s = window.MotobiltSearchSettings || {};

      const alignment = s.productContentAlignment || 'left';
      const border = !!s.productCardBorder;
      const shadow = !!s.productCardShadow;
      const hoverImagesEnabled = !!s.productsHoverImages;
      const quickViewEnabled = s.enableQuickView !== false;
      const quickAddEnabled = s.enableQuickAdd !== false;
      const pricePosition = s.productPricePosition || 'after-product-title';
      const showSku = !!s.showProductsVendor;
      const saleBadgeType = s.saleBadgeType || 'save_amount';
      const badgePosition = s.badgePosition || '';

      const images = product.images || [];
      const primary = images[0] || null;
      const hasSecondary = hoverImagesEnabled && images.length > 1;
      const secondary = hasSecondary ? images[1] : null;

      let ratio = 1;
      if (s.productAspectRatio === 'adapt') {
        ratio = (primary && primary.width && primary.height) ? (primary.width / primary.height) : 1;
      } else if (s.productAspectRatio === 'portrait') {
        ratio = 1 / 1.25;
      } else if (s.productAspectRatio === 'landscape') {
        ratio = 1 / 0.75;
      }
      const paddingBottom = (100 / ratio).toFixed(4);

      let infoSpacing = true;
      const bodyBg = (s.colorBodyBg || '').toLowerCase();
      const cardBg = (s.colorProductCardBg || '').toLowerCase();
      if (bodyBg && (bodyBg === cardBg || cardBg === 'transparent' || cardBg === 'rgba(0,0,0,0)')) {
        if (!border) infoSpacing = false;
        if (shadow) infoSpacing = true;
      }

      const isPurchasable = !!(product.available && product.variantsCount === 1);
      const onSale = !!(product.compareAtPrice && product.compareAtPrice > product.price);

      let badgeHtml = '';
      if (!product.available) {
        badgeHtml = `<span class="badge out-of-stock ${badgePosition}">Sold Out</span>`;
      } else if (onSale) {
        let text;
        if (saleBadgeType === 'save_percentage') {
          text = `Save ${Math.round((1 - product.price / product.compareAtPrice) * 100)}%`;
        } else if (saleBadgeType === 'sale') {
          text = 'Sale';
        } else {
          text = `Save ${this._formatMoney(product.compareAtPrice - product.price)}`;
        }
        badgeHtml = `<span class="badge onsale ${badgePosition}">${text}</span>`;
      }

      const priceHtml = `
        <span class="price">
          ${onSale ? `<del><span class="amount">${this._formatMoney(product.compareAtPrice)}</span></del>` : ''}
          <ins><span class="amount ${onSale ? 'discounted' : ''}">${this._formatMoney(product.price)}</span></ins>
        </span>
      `;

      const titleEscaped = this._escapeHtml(product.title);
      const titleHtml = `<a href="${product.url}" title="${titleEscaped}" class="product-card-title">${titleEscaped}</a>`;

      const skuHtml = showSku && product.sku
        ? `<div class="product-card-sku" style="font-size: 12px; font-family: 'Poppins', sans-serif;">${this._escapeHtml(product.sku)}</div>`
        : '';

      let actionHtml = '';
      if (quickAddEnabled) {
        if (isPurchasable) {
          actionHtml = `
            <button class="product-card--add-to-cart-button product-card--add-to-cart-button-simple button accent" data-product-id="${product.variantId}" tabindex="-1">
              <span class="product-card--add-to-cart-text">Add to Cart</span>
            </button>
          `;
        } else {
          actionHtml = `
            <quick-view class="product-card--add-to-cart-button button accent" data-product-handle="${product.handle}" tabindex="-1">
              <span class="product-card--add-to-cart-text">Choose Options</span>
            </quick-view>
          `;
        }
      }

      const overlaySvg = `
        <div class="custom-overlay">
          <div class="custom-overlay-gradient"></div>
          <svg viewBox="0 0 200 100" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
            <path d="M199.09,70.89L10.49,99.03.91,89.48c-.3-23.13-.61-46.27-.91-69.4v69.78l10.18,10.14,189.82-28.32V0c-.3,23.63-.6,47.27-.91,70.9Z" fill="red"/>
          </svg>
        </div>
      `;

      const imgTag = (img, cls) => img
        ? `<img class="${cls}" width="${img.width || ''}" height="${img.height || ''}" src="${this._resizeImage(img.src, 600)}" alt="${titleEscaped}" loading="lazy">`
        : `<div class="thb-placeholder"></div>`;

      const quickViewHtml = quickViewEnabled
        ? `<quick-view class="product-card-quickview tpo" data-product-handle="${product.handle}" tabindex="-1"><span>Quick View</span></quick-view>`
        : '';

      return `
        <product-card class="product-card product-card--custom text-${alignment} product-card--content-spacing-${infoSpacing} product-card--border-${border} has-shadow--${shadow}">
          <figure class="product-featured-image--2p0 ${hasSecondary ? 'thb-hover' : ''}">
            ${badgeHtml}
            <a href="${product.url}" title="${titleEscaped}" class="product-featured-image--2p0-link aspect-ratio--custom" style="--padding-bottom: ${paddingBottom}%;">
              <div class="custom-image-wrapper">
                ${hasSecondary ? `
                  <div class="secondary-wrapper">
                    ${imgTag(secondary, 'product-secondary-image')}
                    ${overlaySvg}
                  </div>
                ` : ''}
                ${imgTag(primary, 'product-primary-image')}
                ${overlaySvg}
                ${quickViewHtml}
              </div>
            </a>
          </figure>
          <div class="product-card-info--2p0">
            ${pricePosition === 'before-product-title' ? priceHtml : ''}
            ${titleHtml}
            ${skuHtml}
            ${pricePosition !== 'before-product-title' ? priceHtml : ''}
            ${actionHtml}
          </div>
        </product-card>
      `;
    }

    // Compact thumbnail + title row used in the search drawer's product
    // list (reuses the existing .mb-search__product styles from the header
    // dropdown - no price/SKU, just image and title).
    _renderCompactProduct(product) {
      return `
        <a href="${product.url}" class="mb-search__product">
          <div class="mb-search__product-image">
            ${product.image
              ? `<img src="${this._resizeImage(product.image, 80)}" alt="" loading="lazy">`
              : `<div class="mb-search__product-placeholder"></div>`
            }
          </div>
          <div class="mb-search__product-info">
            <div class="mb-search__product-title">${this._highlightMatch(product.title, this.currentQuery)}</div>
          </div>
        </a>
      `;
    }

    _showLoading() {
      if (!this.drawerResults) return;

      this._showResultsContent();

      this.drawerResults.innerHTML = `
        <div class="thb-predictive-search">
          <div class="mb-search__loading">
            <div class="mb-search__spinner"></div>
            <span>Searching...</span>
          </div>
        </div>
      `;
    }

    _renderError() {
      if (!this.drawerResults) return;

      this._showResultsContent();

      this.drawerResults.innerHTML = `
        <div class="thb-predictive-search">
          <div class="mb-search__empty">
            <p>Something went wrong</p>
            <p class="mb-search__empty-hint">Please try again</p>
          </div>
        </div>
      `;
    }

    _submitSearch(query) {
      const q = (query || '').trim();
      if (!q) return;

      window.MotobiltSearch?.recordSearch(q);
      window.location.href = `/search?q=${encodeURIComponent(q)}`;
    }

    // ============================================
    // LEGACY: HEADER DROPDOWN (kept for compatibility)
    // ============================================

    initHeader(options = {}) {
      // Check if using drawer instead
      const drawerInput = document.querySelector('#side-panel-search-input');
      if (drawerInput) {
        console.log('[Motobilt Search] Detected drawer, using initDrawer instead');
        return this.initDrawer({
          inputSelector: '#side-panel-search-input',
          resultsSelector: '[data-search-results]',
          initialSelector: '[data-search-initial]'
        });
      }

      // Original dropdown logic
      this.headerInput = document.querySelector(options.inputSelector || '[data-search-input]');
      this.headerDropdown = document.querySelector(options.dropdownSelector || '[data-search-dropdown]');
      this.headerForm = this.headerInput?.closest('form');

      if (!this.headerInput) {
        console.warn('[Motobilt Search] Input not found');
        return this;
      }

      // Preload products on first focus
      let productsLoaded = false;
      this.headerInput.addEventListener('focus', async () => {
        if (!productsLoaded) {
          productsLoaded = true;
          try {
            await window.MotobiltSearch.loadProducts();
          } catch (e) {
            console.error('Failed to preload products', e);
          }
        }
        this._handleFocus();
      }, { once: false });

      // Input handler with debounce
      this.headerInput.addEventListener('input', (e) => {
        clearTimeout(this.searchDebounce);
        this.searchDebounce = setTimeout(() => {
          this._handleInput(e.target.value);
        }, 150);
      });

      // Keyboard navigation
      this.headerInput.addEventListener('keydown', (e) => this._handleKeydown(e));

      // Form submit
      if (this.headerForm) {
        this.headerForm.addEventListener('submit', (e) => {
          e.preventDefault();
          this._submitSearch(this.headerInput.value);
        });
      }

      // Click outside to close
      document.addEventListener('click', (e) => {
        if (!e.target.closest('[data-search-container]')) {
          this._closeDropdown();
        }
      });

      return this;
    }

    async _handleInput(query) {
      this.currentQuery = query.trim();
      this.selectedIndex = -1;

      if (!this.currentQuery) {
        this._showRecentSearches();
        return;
      }

      if (this.currentQuery.length < 2) {
        return;
      }

      // Show loading state
      this._showDropdownLoading();

      try {
        const results = await window.MotobiltSearch.quickSearch(this.currentQuery);
        this._renderDropdown(results);
      } catch (error) {
        console.error('Search error:', error);
        this._renderDropdownError();
      }
    }

    _handleFocus() {
      if (this.currentQuery && this.currentQuery.length >= 2) {
        this._handleInput(this.currentQuery);
      } else {
        this._showRecentSearches();
      }
    }

    _renderDropdown(results) {
      if (!this.headerDropdown) return;

      let html = '';
      let itemIndex = 0;

      // Typo correction notice
      if (results.correctedQuery) {
        html += `
          <div class="mb-search__correction">
            Showing results for <strong>${results.correctedQuery}</strong>
          </div>
        `;
      }

      // Recent searches (if no query results)
      if (results.recentSearches?.length > 0 && !results.products?.length) {
        html += `
          <div class="mb-search__section">
            <div class="mb-search__section-header">
              <span class="mb-search__section-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
              </span>
              <span>Recent Searches</span>
            </div>
            <div class="mb-search__tags">
              ${results.recentSearches.map(term => `
                <a href="/search?q=${encodeURIComponent(term)}" class="mb-search__tag">${term}</a>
              `).join('')}
            </div>
          </div>
        `;
      }

      // Predictive query suggestions (phrases, not products)
      if (results.querySuggestions?.length > 0) {
        html += `
          <div class="mb-search__section">
            <div class="mb-search__section-header">
              <span class="mb-search__section-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                  <polyline points="17 6 23 6 23 12"></polyline>
                </svg>
              </span>
              <span>Suggested Searches</span>
            </div>
            <div class="mb-search__tags">
              ${results.querySuggestions.map(s => `
                <a href="/search?q=${encodeURIComponent(s.text)}" class="mb-search__tag">
                  ${this._highlightMatch(s.text, this.currentQuery)}
                </a>
              `).join('')}
            </div>
          </div>
        `;
      }

      // Category suggestions
      if (results.suggestions?.length > 0) {
        html += `
          <div class="mb-search__section">
            <div class="mb-search__section-header">
              <span class="mb-search__section-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
              </span>
              <span>Popular Searches</span>
            </div>
            <div class="mb-search__tags">
              ${results.suggestions.map(s => `
                <a href="/search?q=${encodeURIComponent(s.text)}" class="mb-search__tag">
                  ${s.text}
                </a>
              `).join('')}
            </div>
          </div>
        `;
      }

      // Products
      if (results.products?.length > 0) {
        html += `
          <div class="mb-search__section">
            <div class="mb-search__section-header">
              <span class="mb-search__section-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                </svg>
              </span>
              <span>Products</span>
            </div>
            <div class="mb-search__products">
              ${results.products.map(product => `
                <a href="${product.url}" class="mb-search__product" data-item-index="${itemIndex++}">
                  <div class="mb-search__product-image">
                    ${product.image
                      ? `<img src="${this._resizeImage(product.image, 80)}" alt="" loading="lazy">`
                      : `<div class="mb-search__product-placeholder"></div>`
                    }
                  </div>
                  <div class="mb-search__product-info">
                    <div class="mb-search__product-title">${this._highlightMatch(product.title, this.currentQuery)}</div>
                    ${product.sku ? `<div class="mb-search__product-sku">${this._highlightMatch(product.sku, this.currentQuery)}</div>` : ''}
                  </div>
                  <div class="mb-search__product-price">
                    ${product.compareAtPrice && product.compareAtPrice > product.price
                      ? `<span class="mb-search__product-compare">${this._formatMoney(product.compareAtPrice)}</span>`
                      : ''
                    }
                    <span class="mb-search__product-current ${product.compareAtPrice && product.compareAtPrice > product.price ? 'is-sale' : ''}">
                      ${this._formatMoney(product.price)}
                    </span>
                  </div>
                </a>
              `).join('')}
            </div>
          </div>
        `;

        // View all link
        if (results.totalResults > results.products.length) {
          html += `
            <div class="mb-search__footer">
              <a href="/search?q=${encodeURIComponent(this.currentQuery)}" class="mb-search__view-all">
                View all ${results.totalResults} results
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </a>
            </div>
          `;
        }
      }

      // No results
      if (!results.products?.length && this.currentQuery) {
        html = `
          <div class="mb-search__empty">
            <p>No results for "<strong>${this.currentQuery}</strong>"</p>
            <p class="mb-search__empty-hint">Check spelling or try different keywords</p>
          </div>
        `;
      }

      this.headerDropdown.innerHTML = html;
      this._openDropdown();
    }

    _showRecentSearches() {
      if (!this.headerDropdown) return;

      const recent = window.MotobiltSearch?.recentSearches || [];

      if (!recent.length) {
        this._closeDropdown();
        return;
      }

      let itemIndex = 0;

      this.headerDropdown.innerHTML = `
        <div class="mb-search__section">
          <div class="mb-search__section-header">
            <span class="mb-search__section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            </span>
            <span>Recent Searches</span>
            <button type="button" class="mb-search__clear-recent" data-clear-recent>Clear</button>
          </div>
          <div class="mb-search__tags">
            ${recent.slice(0, 8).map(term => `
              <a href="/search?q=${encodeURIComponent(term)}" class="mb-search__tag">${term}</a>
            `).join('')}
          </div>
        </div>
      `;

      // Clear handler
      this.headerDropdown.querySelector('[data-clear-recent]')?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        window.MotobiltSearch.clearRecentSearches();
        this._closeDropdown();
      });

      this._openDropdown();
    }

    _showDropdownLoading() {
      if (!this.headerDropdown) return;

      this.headerDropdown.innerHTML = `
        <div class="mb-search__loading">
          <div class="mb-search__spinner"></div>
          <span>Searching...</span>
        </div>
      `;

      this._openDropdown();
    }

    _renderDropdownError() {
      if (!this.headerDropdown) return;

      this.headerDropdown.innerHTML = `
        <div class="mb-search__empty">
          <p>Something went wrong</p>
          <p class="mb-search__empty-hint">Please try again</p>
        </div>
      `;

      this._openDropdown();
    }

    _openDropdown() {
      if (this.headerDropdown) {
        this.headerDropdown.classList.add('is-open');
        this.isOpen = true;
      }
    }

    _closeDropdown() {
      if (this.headerDropdown) {
        this.headerDropdown.classList.remove('is-open');
        this.isOpen = false;
        this.selectedIndex = -1;
      }
    }

    // ============================================
    // SEARCH RESULTS PAGE
    // ============================================

    initPage(options = {}) {
      const container = document.querySelector(options.containerSelector || '[data-search-page]');
      if (!container) return this;

      this.pageInput = container.querySelector('[data-page-search-input]');
      this.pageResults = container.querySelector('[data-search-results]');
      this.pageFilters = container.querySelector('[data-search-filters]');
      this.pagePagination = container.querySelector('[data-search-pagination]');
      this.pageCount = container.querySelector('[data-search-count]');
      this.pageSort = container.querySelector('[data-search-sort]');
      this.pageContainer = container;

      // Get initial state from URL
      const params = new URLSearchParams(window.location.search);
      this.pageQuery = params.get('q') || '';
      this.pageCurrentPage = parseInt(params.get('page')) || 1;
      this.pageFiltersState = {
        category: params.get('category') || '',
        minPrice: params.get('min_price') || '',
        maxPrice: params.get('max_price') || '',
        inStock: params.get('in_stock') === 'true',
        sort: params.get('sort') || 'relevance'
      };

      // Set initial values
      if (this.pageInput) {
        this.pageInput.value = this.pageQuery;
        this.pageInput.addEventListener('input', this._debounce((e) => {
          this.pageQuery = e.target.value.trim();
          this.pageCurrentPage = 1;
          this._updatePageURL();
          this._performPageSearch();
        }, 300));
      }

      if (this.pageSort) {
        this.pageSort.value = this.pageFiltersState.sort;
        this.pageSort.addEventListener('change', () => {
          this.pageFiltersState.sort = this.pageSort.value;
          this._updatePageURL();
          this._performPageSearch();
        });
      }

      this._initPageFilters();
      this._initMobileFilterToggle();

      // Initial search
      if (this.pageQuery) {
        this._performPageSearch();
      }

      return this;
    }

    _initPageFilters() {
      if (!this.pageContainer) return;

      // Category
      this.pageContainer.querySelectorAll('[data-filter-category]').forEach(el => {
        el.addEventListener('change', () => {
          this.pageFiltersState.category = el.value;
          this.pageCurrentPage = 1;
          this._updatePageURL();
          this._performPageSearch();
        });
      });

      // Price range
      const minPrice = this.pageContainer.querySelector('[data-filter-min-price]');
      const maxPrice = this.pageContainer.querySelector('[data-filter-max-price]');

      if (minPrice) {
        minPrice.value = this.pageFiltersState.minPrice;
        minPrice.addEventListener('change', () => {
          this.pageFiltersState.minPrice = minPrice.value;
          this.pageCurrentPage = 1;
          this._updatePageURL();
          this._performPageSearch();
        });
      }

      if (maxPrice) {
        maxPrice.value = this.pageFiltersState.maxPrice;
        maxPrice.addEventListener('change', () => {
          this.pageFiltersState.maxPrice = maxPrice.value;
          this.pageCurrentPage = 1;
          this._updatePageURL();
          this._performPageSearch();
        });
      }

      // In stock
      const stockFilter = this.pageContainer.querySelector('[data-filter-stock]');
      if (stockFilter) {
        stockFilter.checked = this.pageFiltersState.inStock;
        stockFilter.addEventListener('change', () => {
          this.pageFiltersState.inStock = stockFilter.checked;
          this.pageCurrentPage = 1;
          this._updatePageURL();
          this._performPageSearch();
        });
      }

      // Clear filters
      this.pageContainer.querySelector('[data-clear-filters]')?.addEventListener('click', () => {
        this.pageFiltersState = { sort: 'relevance' };
        this.pageCurrentPage = 1;
        this._resetPageFilterUI();
        this._updatePageURL();
        this._performPageSearch();
      });
    }

    // Mobile-only "Filters" toggle button that expands/collapses the
    // sidebar (Categories/Price/Stock/Clear). The button itself is hidden
    // by CSS on desktop, so this handler is harmless to leave wired there.
    _initMobileFilterToggle() {
      if (!this.pageContainer) return;

      const toggle = this.pageContainer.querySelector('[data-mobile-filter-toggle]');
      const sidebar = this.pageContainer.querySelector('[data-search-filters]');
      if (!toggle || !sidebar) return;

      toggle.addEventListener('click', () => {
        const isOpen = sidebar.classList.toggle('is-open');
        toggle.classList.toggle('is-active', isOpen);
        toggle.setAttribute('aria-expanded', String(isOpen));
      });
    }

    async _performPageSearch() {
      if (!this.pageQuery) {
        this._renderPageEmpty();
        return;
      }

      // Show loading
      if (this.pageResults) {
        this.pageResults.classList.add('is-loading');
      }

      try {
        const options = {
          page: this.pageCurrentPage,
          sort: this.pageFiltersState.sort
        };

        if (this.pageFiltersState.category) {
          options.category = this.pageFiltersState.category;
        }
        if (this.pageFiltersState.minPrice) {
          options.minPrice = parseFloat(this.pageFiltersState.minPrice);
        }
        if (this.pageFiltersState.maxPrice) {
          options.maxPrice = parseFloat(this.pageFiltersState.maxPrice);
        }
        if (this.pageFiltersState.inStock) {
          options.inStock = true;
        }

        const results = await window.MotobiltSearch.search(this.pageQuery, options);

        this._renderPageResults(results);
        this._updatePageFilters(results.categories);
        this._renderPagePagination(results);

        if (this.pageCount) {
          this.pageCount.textContent = results.totalResults;
        }

      } catch (error) {
        console.error('Search error:', error);
      } finally {
        if (this.pageResults) {
          this.pageResults.classList.remove('is-loading');
        }
      }
    }

    _renderPageResults(results) {
      if (!this.pageResults) return;

      if (!results.results?.length) {
        this.pageResults.innerHTML = `
          <div class="mb-page__empty">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <h2>No results found</h2>
            <p>No products match "${this.pageQuery}"</p>
          </div>
        `;
        return;
      }

      let html = '';

      // Correction notice
      if (results.correctedQuery) {
        html += `
          <div class="mb-page__correction">
            Showing results for <strong>${results.correctedQuery}</strong>
          </div>
        `;
      }

      // Product grid
      html += '<div class="mb-page__grid">';

      for (const product of results.results) {
        html += this._renderProductCard(product);
      }

      html += '</div>';

      this.pageResults.innerHTML = html;
    }

    _updatePageFilters(categories) {
      const categorySelect = this.pageContainer?.querySelector('[data-filter-category]');
      if (!categorySelect || !categories) return;

      const currentValue = categorySelect.value;

      categorySelect.innerHTML = `
        <option value="">All Categories</option>
        ${categories.map(cat => `
          <option value="${cat.name}" ${cat.name === currentValue ? 'selected' : ''}>
            ${cat.name} (${cat.count})
          </option>
        `).join('')}
      `;
    }

    _renderPagePagination(results) {
      if (!this.pagePagination) return;

      if (results.totalPages <= 1) {
        this.pagePagination.innerHTML = '';
        return;
      }

      let html = '<div class="mb-pagination">';

      // Previous
      if (results.page > 1) {
        html += `<button class="mb-pagination__btn" data-page="${results.page - 1}">← Previous</button>`;
      }

      // Page numbers
      const pages = this._getPaginationRange(results.page, results.totalPages);
      for (const p of pages) {
        if (p === '...') {
          html += '<span class="mb-pagination__dots">...</span>';
        } else if (p === results.page) {
          html += `<span class="mb-pagination__btn mb-pagination__btn--current">${p}</span>`;
        } else {
          html += `<button class="mb-pagination__btn" data-page="${p}">${p}</button>`;
        }
      }

      // Next
      if (results.hasMore) {
        html += `<button class="mb-pagination__btn" data-page="${results.page + 1}">Next →</button>`;
      }

      html += '</div>';

      this.pagePagination.innerHTML = html;

      // Click handlers
      this.pagePagination.querySelectorAll('[data-page]').forEach(btn => {
        btn.addEventListener('click', () => {
          this.pageCurrentPage = parseInt(btn.dataset.page);
          this._updatePageURL();
          this._performPageSearch();
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });
      });
    }

    _getPaginationRange(current, total) {
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

      if (current <= 4) return [1, 2, 3, 4, 5, '...', total];
      if (current >= total - 3) return [1, '...', total - 4, total - 3, total - 2, total - 1, total];

      return [1, '...', current - 1, current, current + 1, '...', total];
    }

    _renderPageEmpty() {
      if (this.pageResults) {
        this.pageResults.innerHTML = `
          <div class="mb-page__empty">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <h2>Search our products</h2>
            <p>Enter a search term to find products</p>
          </div>
        `;
      }

      if (this.pagePagination) this.pagePagination.innerHTML = '';
      if (this.pageCount) this.pageCount.textContent = '0';
    }

    _resetPageFilterUI() {
      this.pageContainer?.querySelectorAll('[data-filter-category]').forEach(el => el.value = '');

      const minPrice = this.pageContainer?.querySelector('[data-filter-min-price]');
      if (minPrice) minPrice.value = '';

      const maxPrice = this.pageContainer?.querySelector('[data-filter-max-price]');
      if (maxPrice) maxPrice.value = '';

      const stockFilter = this.pageContainer?.querySelector('[data-filter-stock]');
      if (stockFilter) stockFilter.checked = false;

      if (this.pageSort) this.pageSort.value = 'relevance';
    }

    _updatePageURL() {
      const params = new URLSearchParams();

      if (this.pageQuery) params.set('q', this.pageQuery);
      if (this.pageCurrentPage > 1) params.set('page', this.pageCurrentPage);
      if (this.pageFiltersState.category) params.set('category', this.pageFiltersState.category);
      if (this.pageFiltersState.minPrice) params.set('min_price', this.pageFiltersState.minPrice);
      if (this.pageFiltersState.maxPrice) params.set('max_price', this.pageFiltersState.maxPrice);
      if (this.pageFiltersState.inStock) params.set('in_stock', 'true');
      if (this.pageFiltersState.sort && this.pageFiltersState.sort !== 'relevance') {
        params.set('sort', this.pageFiltersState.sort);
      }

      const newURL = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({}, '', newURL);
    }

    // ============================================
    // UTILITIES
    // ============================================

    _highlightMatch(text, query) {
      if (!query || !text) return text;
      const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escaped})`, 'gi');
      return text.replace(regex, '<mark>$1</mark>');
    }

    _escapeHtml(text) {
      if (!text) return '';
      return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    _formatMoney(amount) {
      if (typeof amount !== 'number') return '';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(amount);
    }

    _resizeImage(url, size) {
      if (!url) return '';
      return url.replace(/(_\d+x\d+)?(\.[a-zA-Z]+)(\?.*)?$/, `_${size}x$2$3`);
    }

    _debounce(fn, delay) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn.apply(this, args), delay);
      };
    }
  }

  // Global export
  window.MotobiltSearchUI = new MotobiltSearchUI();

})();
